<template>
  <div class="flex justify-center">
    <QuoteActionsMenu
      :options="tippyOptions"
      :quote="quote"
      :tippy-options="tippyOptions"
      @quote-approve-click="toggleApprovalQuoteModal"
      @quote-force-approve-click="toggleForceApprovalQuoteModal"
      @schedule-work-click="toggleScheduleModal"
      @decline-quote-click="toggleDeclineQuoteModal"
      @edit-quote-click="handleEditQuote"
      @email-quote-click="handleEmailQuote"
      @archive-quote-click="handleArchive"
      @unarchive-quote-click="handleUnarchive"
      @pending-quote-click="handleReadyQuote"
      @draft-quote-click="handleDraftQuote"
      @clone-quote-click="handleCloneQuote"
      @reactivate-quote-click="handleReactivateQuote"
      @pdf-quote-click="handleDownloadPDF"
      @unapprove-quote-click="handleUnapproveQuote"
    >
      <template #trigger>
        <slot name="trigger"></slot>
      </template>
      <template #footer>
        <div
          v-if="allowToggleDisplayMembershipSavings"
          class="border-t-[1px] border-gray-300"
        >
          <div
            class="flex justify-between align-center whitespace-nowrap px-5 py-4"
          >
            <span class="!text-gray-700 font-medium text-[12px]"
              >Show Membership Savings</span
            >
            <ToggleSwitch
              v-model="displayMembershipSavings"
              data-cy="display-membership-savings-toggle"
              @update:modelValue="handleToggleMembershipSavings"
            />
          </div>
        </div>
      </template>
    </QuoteActionsMenu>
    <DeclineQuoteModal
      v-if="declineQuoteModal"
      :quote-data="quote"
      @hide-modal="toggleDeclineQuoteModal"
    />
    <QuoteApprovalModal
      v-if="approvalModal"
      :quote="quote"
      @hide-modal="toggleApprovalQuoteModal"
    />
    <quote-force-approval-modal
      v-if="forceApprovalModal"
      :quote="quote"
      @hide-modal="toggleForceApprovalQuoteModal"
    ></quote-force-approval-modal>
    <schedule-job-modal
      v-if="scheduleModal"
      :address="address"
      :customer="customer"
      :link="scheduleLink"
      @hide-modal="toggleOffScheduleModal"
    ></schedule-job-modal>
    <EmailQuoteOrInvoiceModal
      v-if="showEmailQuoteModal"
      :quote="quote"
      @hide-modal="showEmailQuoteModal = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import ToggleSwitch from "@/components/ToggleSwitch.vue";
import QuoteActionsMenu from "@/Customer/Profile/QuoteActionsMenu.vue";
import EmailQuoteOrInvoiceModal from "@/EmailQuoteOrInvoice/EmailQuoteOrInvoiceModal.vue";
import DeclineQuoteModal from "@/Job/QuoteBuilder/DeclineQuoteModal.vue";
import QuoteApprovalModal from "@/Job/QuoteBuilder/QuoteApprovalModal.vue";
import QuoteForceApprovalModal from "@/Job/QuoteBuilder/QuoteForceApprovalModal.vue";
import ScheduleJobModal from "@/ScheduleModal/ScheduleJobModal.vue";

export default {
  components: {
    EmailQuoteOrInvoiceModal,
    DeclineQuoteModal,
    QuoteApprovalModal,
    QuoteForceApprovalModal,
    ScheduleJobModal,
    QuoteActionsMenu,
    ToggleSwitch
  },
  props: {
    quote: {
      type: Object,
      default: () => ({})
    },
    tippyOptions: {
      type: Object,
      default: () => ({})
    },
    hideMembershipSavingsToggle: {
      type: Boolean,
      default: false
    }
  },
  emits: ["edit-quote"],
  data() {
    return {
      modalOpen: false,
      declineQuoteModal: false,
      approvalModal: false,
      forceApprovalModal: false,
      scheduleModal: false,
      quoteData: {},
      showEmailQuoteModal: false,
      displayMembershipSavings: this.quote.display_membership_savings
    };
  },
  computed: {
    ...mapState(["job", "tenant"]),
    address() {
      return this.quoteData.service_address;
    },
    customer() {
      return this.quoteData.customer;
    },
    scheduleLink() {
      return this.quoteData.links.quote_schedule;
    },
    allowToggleDisplayMembershipSavings() {
      return (
        this.featureEnabledForTenant("toggle_display_membership_savings") &&
        this.tenant.display_membership_savings_ad_hoc &&
        !this.hideMembershipSavingsToggle &&
        this.quote.status !== "approved" &&
        this.quote.links.quote_update
      );
    }
  },
  methods: {
    ...mapActions([
      "confirmQuoteChange",
      "archiveQuote",
      "unarchiveQuote",
      "setQuoteState",
      "setCurrentQuote",
      "cloneQuote",
      "unapproveQuoteSignature",
      "fetchQuote",
      "updateQuoteDisplayMembershipSavings"
    ]),
    ...mapGetters(["featureEnabledForTenant"]),
    toggleApprovalQuoteModal() {
      this.approvalModal = !this.approvalModal;
    },
    toggleForceApprovalQuoteModal() {
      this.forceApprovalModal = !this.forceApprovalModal;
    },
    toggleDeclineQuoteModal() {
      if (!this.declineQuoteModal) {
        this.confirmQuoteChange(this.quote).then(
          () => (this.declineQuoteModal = !this.declineQuoteModal)
        );
      } else {
        this.declineQuoteModal = !this.declineQuoteModal;
      }
    },
    toggleScheduleModal() {
      this.fetchQuote({
        quoteId: this.quote.id,
        customerId: this.quote.customer_id
      }).then((res) => {
        this.quoteData = res.data;
        this.scheduleModal = true;
      });
    },
    toggleOffScheduleModal() {
      this.scheduleModal = false;
    },
    handleEditQuote() {
      //handles single quote edition
      this.$emit("edit-quote");
      //handles datatable quote edition
      EventBus.$emit("datatable-edit-quote", {
        quoteId: this.quote.id,
        editing: true,
        customerId: this.quote.customer_id
      });
    },
    handleEmailQuote() {
      this.showEmailQuoteModal = true;
    },
    handleArchive() {
      this.setQuoteState(this.quote.links.quote_archive).then(() => {
        this.handleSuccessAction(
          `Quote ${this.quote.id} archived successfully!`
        );
      });
    },
    handleUnarchive() {
      this.setQuoteState(this.quote.links.quote_unarchive).then(() => {
        this.handleSuccessAction(
          `Quote ${this.quote.id} unarchived successfully!`
        );
      });
    },
    handleReadyQuote() {
      this.setQuoteState(this.quote.links.quote_pending).then(() => {
        this.handleSuccessAction(
          `Quote ${this.quote.id} has been set to open successfully!`
        );
      });
    },
    handleCloneQuote() {
      this.cloneQuote(this.quote.links.quote_clone).then(() => {
        this.handleSuccessAction(
          `Quote ${this.quote.id} has been cloned successfully!`
        );
      });
    },
    handleDraftQuote() {
      this.setQuoteState(this.quote.links.quote_draft).then(() => {
        this.handleSuccessAction(
          `Quote ${this.quote.id} has been set to draft successfully!`
        );
      });
    },
    handleReactivateQuote() {
      this.setQuoteState(this.quote.links.quote_reactivate).then(() => {
        this.handleSuccessAction(
          `Quote ${this.quote.id} has been reactivated successfully!`
        );
      });
    },
    handleUnapproveQuote() {
      this.unapproveQuoteSignature(this.quote.links.quote_break).then(() => {
        this.handleSuccessAction(
          `Quote ${this.quote.id} is no longer approved. Please obtain a new quote acceptance signature from the customer`
        );
      });
    },
    handleToggleMembershipSavings() {
      this.updateQuoteDisplayMembershipSavings({
        url: this.quote.links.quote_update,
        display_membership_savings: this.displayMembershipSavings
      }).then(() => {
        window.notify.success(
          `Quote ${this.quote.id} membership savings configuration successfully updated!`
        );
      });
    },
    handleSuccessAction(message) {
      window.notify.success(message);
      EventBus.$emit("refresh-quote-datatable");
      this.setCurrentQuote(null);
    },
    handleDownloadPDF() {
      window.open(this.quote.links.quote_pdf);
    }
  }
};
</script>

<style lang="scss">
#quotes-table .fa-ellipsis-v {
  color: var(--primary-color);
}
</style>
